import { useEffect, useState } from 'react';
import { MediaTypes } from '@/constants';
import Course33Media, { Course33MediaSlot } from '@/models/course-33-media';
import { Course33MediaPayload, useCreateCourse33Media, useUpdateCourse33Media, useDeleteCourse33Media } from '@/services/course-33-media';
import FormModal from '@/shared/components/form-modal';
import DropZoneField from '@/shared/components/form/dropzones/DropZone';
import { MaterialFormContainer, MaterialFormOption, MaterialTextField } from '@/shared/components/form/MaterialFormElements';
import { errorBannerStyle } from '@/shared/dark-inline-styles';

export type MediaSectionMode = 'day0video' | 'dayMedia';

// Course-33 runs across 21 program days (morning/night live on 1..21).
const PROGRAM_DAYS = Array.from({ length: 21 }, (_, i) => i + 1);

interface MediaModalProps {
    isOpen: boolean;
    toggle: () => void;
    mode: MediaSectionMode;
    // The row being edited (undefined = create). Passed from the list — the API
    // returns full docs, so there's no per-id fetch.
    item?: Course33Media;
}

interface MediaForm {
    day: number;
    slot: Course33MediaSlot;
    title: string;
    caption: string;
    mediaPath: string;
    mediaType: MediaTypes;
    orderIndex: number | '';
}

const emptyForm = (mode: MediaSectionMode): MediaForm => ({
    day: mode === 'day0video' ? 0 : 1,
    slot: mode === 'day0video' ? 'dayZeroVideo' : 'morning',
    title: '',
    caption: '',
    mediaPath: '',
    mediaType: mode === 'day0video' ? MediaTypes.Video : MediaTypes.Audio,
    orderIndex: '',
});

const Hint = ({ children }: { children: React.ReactNode }) => (
    <p style={{ margin: '-6px 0 10px', fontSize: 12, color: '#999999', lineHeight: 1.5 }}>{children}</p>
);

const MediaModal = ({ isOpen, toggle, mode, item }: MediaModalProps) => {
    const { mutateAsync: createMedia, isPending: isCreating } = useCreateCourse33Media();
    const { mutateAsync: updateMedia, isPending: isUpdating } = useUpdateCourse33Media();
    const { mutateAsync: deleteMedia, isPending: isDeleting } = useDeleteCourse33Media();

    const [form, setForm] = useState<MediaForm>(emptyForm(mode));
    const [error, setError] = useState<string | null>(null);
    const [uploadingCount, setUploadingCount] = useState(0);
    const onUploadingChange = (uploading: boolean) => setUploadingCount((c) => Math.max(0, c + (uploading ? 1 : -1)));
    const isUploading = uploadingCount > 0;

    // `isOpen` in the deps so re-opening the SAME row re-fills from the passed item
    // (the close-reset below would otherwise leave it blank).
    useEffect(() => {
        if (!isOpen) return;
        if (item) {
            setForm({
                day: item.day,
                slot: item.slot,
                title: item.title,
                caption: item.caption ?? '',
                mediaPath: item.mediaPath,
                mediaType: item.mediaType,
                orderIndex: typeof item.orderIndex === 'number' ? item.orderIndex : '',
            });
        } else {
            setForm(emptyForm(mode));
        }
        setError(null);
    }, [isOpen, item, mode]);

    useEffect(() => {
        if (!isOpen) {
            setForm(emptyForm(mode));
            setError(null);
            setUploadingCount(0);
        }
    }, [isOpen, mode]);

    const set = <K extends keyof MediaForm>(key: K, value: MediaForm[K]) => setForm((f) => ({ ...f, [key]: value }));

    const mediaLabel = mode === 'day0video' ? 'video' : 'audio';
    const acceptTypes = mode === 'day0video' ? 'video/mp4' : 'audio/mp3, audio/mpeg, video/mp4';

    const validate = (): string | null => {
        if (!form.title.trim()) return 'Title is required';
        if (!form.mediaPath) return `Upload a ${mediaLabel} first (it must finish processing).`;
        if (form.orderIndex !== '' && !Number.isInteger(Number(form.orderIndex))) return 'Order must be a whole number.';
        return null;
    };

    const buildPayload = (): Course33MediaPayload => ({
        day: form.day,
        slot: form.slot,
        title: form.title.trim(),
        // Send '' (not undefined) so CLEARING a caption on edit actually removes it —
        // the API accepts an empty caption and the app hides a falsy one.
        caption: form.caption.trim(),
        mediaPath: form.mediaPath,
        mediaType: form.mediaType,
        orderIndex: form.orderIndex === '' ? undefined : Number(form.orderIndex),
    });

    const handleSave = async () => {
        if (isUploading) {
            setError(`Please wait — the ${mediaLabel} is still uploading.`);
            return;
        }
        const validationError = validate();
        if (validationError) {
            setError(validationError);
            return;
        }
        // callApi never throws on API errors — inspect result.status.
        const payload = buildPayload();
        const result = item ? await updateMedia({ mediaId: item.id, ...payload }) : await createMedia(payload);
        if (result.status === 'error') {
            setError(result.error?.message ?? 'Failed to save.');
            return;
        }
        toggle();
    };

    const handleDelete = async () => {
        if (!item) return;
        const result = await deleteMedia({ mediaId: item.id });
        if (result.status === 'error') {
            setError(result.error?.message ?? 'Failed to delete.');
            return;
        }
        toggle();
    };

    const title = `${item ? 'Edit' : 'New'} ${mode === 'day0video' ? 'Day-0 video' : 'day media'}`;

    return (
        <FormModal
            isOpen={isOpen}
            showDelete={!!item}
            title={title}
            body={
                <MaterialFormContainer onSubmit={handleSave}>
                    {error && <div style={errorBannerStyle}>{error}</div>}

                    {mode === 'dayMedia' && (
                        <div style={{ display: 'flex', gap: 16 }}>
                            <MaterialTextField name="select" select label="Day" value={String(form.day)} onChange={(e) => set('day', Number(e.target.value))}>
                                {PROGRAM_DAYS.map((d) => (
                                    <MaterialFormOption key={d} value={String(d)}>
                                        Day {d}
                                    </MaterialFormOption>
                                ))}
                            </MaterialTextField>
                            <MaterialTextField
                                name="select"
                                select
                                label="Slot"
                                value={form.slot}
                                onChange={(e) => set('slot', e.target.value as Course33MediaSlot)}
                            >
                                <MaterialFormOption value="morning">Morning — daily affirmation</MaterialFormOption>
                                <MaterialFormOption value="night">Night</MaterialFormOption>
                            </MaterialTextField>
                        </div>
                    )}

                    <MaterialTextField label="Title" value={form.title} onChange={(e) => set('title', e.target.value)} />

                    <MaterialTextField label="Caption (optional)" multiline value={form.caption} onChange={(e) => set('caption', e.target.value)} />
                    <Hint>The short line shown under the {mediaLabel} on the day page.</Hint>

                    {/* ── Media (Mux) ─────────────────────────────────────── */}
                    <p style={{ margin: '18px 0 4px', fontWeight: 600 }}>{mode === 'day0video' ? 'Video' : 'Audio'}</p>
                    <Hint>
                        {mode === 'day0video'
                            ? 'The orientation video. Streamed via Mux — the upload keeps going even if it looks slow; wait until it finishes before saving.'
                            : 'The morning/night audio. Streamed via Mux. Wait until the upload finishes before saving.'}
                    </Hint>
                    <DropZoneField
                        customHeight
                        accept={acceptTypes}
                        currentValue={form.mediaPath}
                        currentFileType={form.mediaType}
                        onChange={(mediaPath, mediaType) => {
                            set('mediaPath', mediaPath);
                            if (mediaType) set('mediaType', mediaType);
                        }}
                        onUploadingChange={onUploadingChange}
                    />

                    <MaterialTextField
                        label="Order"
                        type="number"
                        inputProps={{ step: 1 }}
                        value={form.orderIndex}
                        onChange={(e) => set('orderIndex', e.target.value === '' ? '' : Number(e.target.value))}
                    />
                    <Hint>Lower numbers show first{mode === 'day0video' ? ' in the accordion' : ''}. Leave blank to append.</Hint>
                </MaterialFormContainer>
            }
            toggle={toggle}
            handleSave={handleSave}
            handleDelete={handleDelete}
            loading={isCreating || isUpdating || isDeleting}
            saveDisabled={isUploading}
            saveDisabledHint={`Uploading ${mediaLabel}… please wait until it finishes before saving.`}
        />
    );
};

export default MediaModal;
